import { Suspense } from "react";

import { AgentsIcon } from "@hightouch/htui";

import {
  AgentSidebar,
  type AgentSidebarPanel,
} from "src/components/layout/page-sidebar/agent/agent-sidebar";
import type { ResourceAgentConfig } from "src/components/layout/page-sidebar/agent/resource-agent-sidebar/types";
import { buildAgentSidebarStorageKey } from "src/components/layout/page-sidebar/agent/use-agent-sidebar-state";
import { EmbeddedAgentInitialViewSkeleton } from "src/components/llm/embedded-chat/embedded-agent-initial-view";
import type { FocusPortal } from "src/pages/agents/session";
import { lazyNamed } from "src/utils/lazy-named";

// The chat body (llm-chat and friends) stays out of the page route's static
// chunk until the sidebar renders.
const ResourceAgentSidebarContent = lazyNamed(
  () =>
    import(
      "src/components/layout/page-sidebar/agent/resource-agent-sidebar/resource-agent-sidebar-content"
    ),
  "ResourceAgentSidebarContent",
);

const PANELS: AgentSidebarPanel<"agent">[] = [
  {
    id: "agent",
    icon: AgentsIcon,
    label: "Open agent panel",
    name: "agent",
    hotkey: "shift+a",
    showsAgentActivity: true,
  },
];

type Props = {
  config: ResourceAgentConfig;
  enabled?: boolean;
  /** Expands the listed message types into a page panel beside the docked chat instead of a drawer over it. */
  focusPanel?: FocusPortal;
  /** Keeps the chat docked below xl, such as beside a review panel the host shows. */
  forceDocked?: boolean;
  /** Reports the width the sidebar takes from the page's right edge, for content aligned beside it. */
  onRightInsetChange?: (rightInsetPx: number) => void;
  collapseByDefault?: boolean;
};

/**
 * A page's agent sidebar: the generic `AgentSidebar` with a single Agent panel
 * whose chats are scoped to one resource (an identity graph, a journey).
 */
export function ResourceAgentSidebar({
  config,
  enabled = true,
  focusPanel,
  forceDocked,
  onRightInsetChange,
  collapseByDefault,
}: Props) {
  const storageKey = buildAgentSidebarStorageKey({
    prefix: config.sidebarStorageKeyPrefix,
    resourceId: config.resourceId,
    useGlobalKey: false,
  });
  return (
    <AgentSidebar
      enabled={enabled}
      defaultPanel="agent"
      storageKey={storageKey}
      panels={PANELS}
      name={config.sidebarName}
      forceDocked={forceDocked}
      onRightInsetChange={onRightInsetChange}
      collapseByDefault={collapseByDefault}
    >
      {({
        isOpen,
        isDocked,
        setOpenTemporarily,
        initialSessionId,
        onSessionChange,
        onAgentActivityChange,
        collapseButton,
      }) => (
        <Suspense fallback={<EmbeddedAgentInitialViewSkeleton />}>
          <ResourceAgentSidebarContent
            config={config}
            isOpen={isOpen}
            storageKey={storageKey}
            isDocked={isDocked}
            focusPanel={focusPanel}
            setOpen={setOpenTemporarily}
            initialSessionId={initialSessionId}
            onSessionChange={onSessionChange}
            onAgentActivityChange={onAgentActivityChange}
            collapseButton={collapseButton}
          />
        </Suspense>
      )}
    </AgentSidebar>
  );
}
